import { AsyncPipe, CommonModule } from '@angular/common';
import { Component, forwardRef, inject, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextPipe } from 'angular-i18next';
import { WizardComponent, WizardStepComponent } from 'mbs-ui-kit';
import { HelpService } from 'mbs-ui-shared@help/help.service';
import { first } from 'rxjs/operators';
import { IBBModelForStepsHelper } from 'wizards/helpers/image-based-wizard-helpers/ibb-model-for-steps-helper';
import { IBBPlanFromStepsHelper } from 'wizards/helpers/image-based-wizard-helpers/ibb-plan-from-steps-helper';
import { PlanResponseModel } from 'wizards/models/base/plan-response-model';
import { WizardHelpLink } from 'wizards/remote-management-wizards/strategy/wizard-help-link.class';
import { WizardStepsService } from 'wizards/services/wizard-steps.service';
import { AdvancedOptionsStepComponent } from 'wizards/steps/advanced-options-step/advanced-options-step.component';
import { ConsistencyStepComponent } from 'wizards/steps/archive-consistency-step/consistency-step.component';
import { DestinationPlusRetentionStepComponent } from 'wizards/steps/combined/destination-plus-retention-step/destination-plus-retention-step.component';
import { CompressionEncryptionStepComponent } from 'wizards/steps/compression-encryption-step/compression-encryption-step.component';
import { NotificationsStepComponent } from 'wizards/steps/notifications-step/notifications-step.component';
import { PlanNameStepComponent } from 'wizards/steps/plan-name-step/plan-name-step.component';
import { PrePostActionsStepComponent } from 'wizards/steps/pre-post-actions-step/pre-post-actions-step.component';
import { NBFRetentionPolicyStepComponent } from 'wizards/steps/retention-policy-step/NBF/retention-policy-step.component';
import { RetentionPolicyStepComponent } from 'wizards/steps/retention-policy-step/retention-policy-step.component';
import { ScheduleAdvancedStepComponent } from 'wizards/steps/schedule-advanced-step/schedule-advanced-step.component';
import { NBFScheduleStepComponent } from 'wizards/steps/schedule-step/NBF/schedule-step.component';
import { ScheduleStepComponent } from 'wizards/steps/schedule-step/schedule-step.component';
import { SimpleScheduleStepComponent } from 'wizards/steps/simple-schedule-step/simple-schedule-step.component';
import { StepLoadInfo } from 'wizards/steps/StepBase.class';
import { WhatBackupStepComponent } from 'wizards/steps/what-backup-step/what-backup-step.component';
import { WhereBackupStepComponent } from 'wizards/steps/where-backup-step/where-backup-step.component';
import { IBBFormModel } from '../../models/ibb/ibb-form-model';
import { WizardsServicesHubService } from '../../services/wizards-services-hub.service';
import { WizardBaseClass } from '../wizard-base.class';

type IBBWizardHelpDocumentationLinks =
  | 'welcome'
  | 'whereToBackUp'
  | 'destinationPlusRetention'
  | 'whatToBackUp'
  | 'advancedOptions'
  | 'compressionEncryption'
  | 'fullConsistencyCheck'
  | 'schedule'
  | 'retention'
  | 'prePost'
  | 'notification';

@UntilDestroy()
@Component({
  selector: 'mbs-image-based-wizard',
  templateUrl: './image-based-wizard.component.html',
  providers: [
    {
      provide: WizardBaseClass,
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      useExisting: forwardRef(() => ImageBasedWizardComponent)
    }
  ],
  imports: [
    AdvancedOptionsStepComponent,
    AsyncPipe,
    CommonModule,
    ConsistencyStepComponent,
    FormsModule,
    NBFRetentionPolicyStepComponent,
    NBFScheduleStepComponent,
    NotificationsStepComponent,
    PlanNameStepComponent,
    PrePostActionsStepComponent,
    RetentionPolicyStepComponent,
    ScheduleAdvancedStepComponent,
    ScheduleStepComponent,
    SimpleScheduleStepComponent,
    WhatBackupStepComponent,
    WhereBackupStepComponent,
    WizardComponent,
    WizardStepComponent,
    I18NextPipe,
    CompressionEncryptionStepComponent,
    DestinationPlusRetentionStepComponent
  ]
})
export class ImageBasedWizardComponent extends WizardBaseClass {
  i18nPipe: I18NextPipe;

  public restoreVerificationEnabled = true;
  public fromGettingStarted = false;
  public helpLink: WizardHelpLink<IBBWizardHelpDocumentationLinks>;

  @ViewChild(WizardComponent, { static: true }) wizard: WizardComponent;
  constructor() {
    const services = inject(WizardsServicesHubService);
    const i18nPipe = inject(I18NextPipe);
    const stepService = inject(WizardStepsService);
    const helpService = inject(HelpService);

    super();
    this.i18nPipe = i18nPipe;

    this.fromGettingStarted = services.mainService.fromGettingStarted;

    this.FormModel = new IBBFormModel(this.isNBF, this.isUnix && !this.isNetBuild);
    if (!this.isRDMode || (this.isRDMode && this.isEdit)) {
      this.myPlan$
        .pipe(
          first((p) => !!p),
          untilDestroyed(this)
        )
        .subscribe((plan) => plan && this.updateStepData(plan));
      return;
    }
    this.updateStepData(new PlanResponseModel(), true);
  }

  updateStepData(plan, isDefault = false): void {
    this.setLoadedPlanAndRunChainRequestsForBackup(plan);

    if (isDefault) {
      // TODO: MBS-24778 Enable new schedule and retention in Backup Templates
      if (!this.isEdit && this.isNBF && (!this.isUnix || this.isNetBuild)) {
        this.FormModel.scheduleStep.ScheduleType = 'noschedule';
        this.FormModel.scheduleStep.fullBackupData.enabled = false;
      }

      return this.setDefaultValue();
    }

    const formData = IBBModelForStepsHelper.IBBModelFromPlan(plan, this.defaultSettings());

    if (this.isRDMode) formData.advancedOptionsFlags = this.FormModel.advancedOptionsFlags;

    this.FormModel = formData;

    // TODO: MBS-24778 Enable new schedule and retention in Backup Templates
    const rightVersionForSchedule = this.mainService.backupVersionUpdated && +this.mainService.backupVersionUpdated.substring(0, 2) >= 80;
    if (!this.isEdit && this.isNBF && (!this.isUnix || this.isNetBuild) && !rightVersionForSchedule) {
      this.FormModel.scheduleStep.ScheduleType = 'noschedule';
      this.FormModel.scheduleStep.fullBackupData.enabled = false;
    }
  }

  restoreVerificationEnabledHandler(event: boolean): void {
    this.restoreVerificationEnabled = event;
  }

  protected loadInfoHandlerFinally(event?: StepLoadInfo) {
    if (this.stepsManager.currentStep.title === this.stepsManager.titles.consistency && (!event || !event.loading) && event.isHyperV) {
      this.stepsManager.next();
      this.stepsManager.setCurrentStep(this.stepsManager.titles.schedule, true);
    }
  }

  protected getPlanForSaving(): any {
    return IBBPlanFromStepsHelper.getUpdatedPlan(this.loadedPlan.plan, this.FormModel, this.defaultSettings(), this.isNBF, this.isUnix);
  }

  getRAWHelpLinks(): Record<IBBWizardHelpDocumentationLinks, string> {
    return {
      welcome: 'backup/ibb/nbf#plan',
      whereToBackUp: 'backup/ibb/nbf#where',
      destinationPlusRetention: 'backup/ibb/nbf#where',
      whatToBackUp: 'backup/ibb/nbf#what',
      advancedOptions: 'backup/ibb/nbf#aoptions',
      compressionEncryption: 'backup/ibb/nbf#compression',
      fullConsistencyCheck: 'backup/ibb/nbf#fcc',
      schedule: 'backup/ibb/nbf#schedule',
      retention: this.isNBF ? 'backup/ibb/nbf#retention' : 'backup/ibb/legacy#retention',
      prePost: 'backup/ibb/nbf#prepost',
      notification: 'backup/ibb/nbf#notifications'
    };
  }
}
